import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Calendar } from 'react-feather';
import { Button } from '../../components/ui/button';
import { FeatureTag } from '../../components/ui/feature-tag';
import { FeatherArrowLeft, FeatherChevronLeft, FeatherChevronRight, FeatherCheckCircle, FeatherPrinter } from '../../components/ui/icons';
import DateSelector from '../../components/DailyReview/DateSelector';
import ResourceReviewCard from '../../components/DailyReview/ResourceReviewCard';
import ProjectReviewReportModal from '../../components/DailyReview/Report';
import NotesBottomBar from '../../components/DailyReview/NotesBottomBar';
import dailyReviewService from '../../services/dailyReviewService';
import { releasesApi } from '../../services/api';
import { getTodayLocalDate } from '../../utils/date';
import './ResourceReviewFlow.css';

const ResourceReviewFlow = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [date, setDate] = useState(() => {
        const dateParam = searchParams.get('date');
        const today = getTodayLocalDate();
        // If no date in URL or date is in the future, use today's date
        if (!dateParam) {
            return today;
        }
        // Validate date is not in the future
        if (dateParam > today) {
            return today;
        }
        return dateParam;
    });
    
    const [reviewList, setReviewList] = useState(null);
    const [currentResourceIndex, setCurrentResourceIndex] = useState(0);
    const [currentResourceData, setCurrentResourceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingResource, setLoadingResource] = useState(false);
    const [error, setError] = useState(null);
    const [completed, setCompleted] = useState(false);
    const [reviewMarked, setReviewMarked] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [reviewNotes, setReviewNotes] = useState({
        overallAssessment: '',
        issues: '',
        actionItems: '',
        resourceObservations: '',
        taskConcerns: '',
        featureTagNotes: '',
        additionalComments: '',
        goals: {
            tasks: [],
            features: [],
            personal: [],
            noGoals: false
        }
    });
    const [notesError, setNotesError] = useState('');
    const [isNotesBarExpanded, setIsNotesBarExpanded] = useState(false);
    const [releases, setReleases] = useState([]);
    const saveTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Sync date from URL params when they change, and ensure URL has a valid date
    useEffect(() => {
        const dateParam = searchParams.get('date');
        const today = getTodayLocalDate();
        
        // If no date in URL or date is in the future, update URL with today's date
        if (!dateParam || dateParam > today) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('date', today);
            setSearchParams(newSearchParams, { replace: true });
            if (date !== today) {
                setDate(today);
            }
        } else if (dateParam !== date) {
            // URL has a valid date that's different from state, update state
            setDate(dateParam);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        fetchReviewList();
        fetchReleases();
    }, [projectId, date]);

    const fetchReleases = async () => {
        try {
            const releasesData = await releasesApi.getProjectReleases(projectId);
            setReleases(releasesData || []);
        } catch (err) {
            console.error('Error fetching releases:', err);
        }
    };

    useEffect(() => {
        if (reviewList && reviewList.allResources.length > 0) {
            fetchCurrentResource();
        }
    }, [reviewList, currentResourceIndex]);

    // Load notes from backend when component mounts
    useEffect(() => {
        const loadNotes = async () => {
            try {
                const summaryData = await dailyReviewService.getProjectSummary(projectId, date);
                if (summaryData && summaryData.reviewNotes) {
                    setReviewNotes(summaryData.reviewNotes);
                } else {
                    // Initialize with empty notes if no review exists
                    setReviewNotes({
                        overallAssessment: '',
                        issues: '',
                        actionItems: '',
                        resourceObservations: '',
                        taskConcerns: '',
                        featureTagNotes: '',
                        additionalComments: '',
                        goals: {
                            tasks: [],
                            features: [],
                            personal: [],
                            noGoals: false
                        }
                    });
                }
            } catch (err) {
                console.error('Error loading notes:', err);
                // Don't show error to user, just use empty notes
                setReviewNotes({
                    overallAssessment: '',
                    issues: '',
                    actionItems: '',
                    resourceObservations: '',
                    taskConcerns: '',
                    featureTagNotes: '',
                    additionalComments: '',
                    goals: {
                        tasks: [],
                        features: [],
                        personal: [],
                        noGoals: false
                    }
                });
            }
        };
        
        if (projectId && date) {
            loadNotes();
        }
    }, [projectId, date]);

    const fetchReviewList = async () => {
        try {
            setLoading(true);
            setError(null);
            setCompleted(false);
            setCurrentResourceIndex(0);
            const data = await dailyReviewService.getResourceReviewList(projectId, date);
            
            // Combine resources with and without work plans into a single array
            const allResources = [
                ...(data.resourcesWithWorkPlans || []).map(r => ({ ...r, hasWorkPlan: true })),
                ...(data.resourcesWithoutWorkPlans || []).map(r => ({ ...r, hasWorkPlan: false }))
            ];
            
            setReviewList({
                ...data,
                allResources
            });
        } catch (err) {
            console.error('Error fetching resource review list:', err);
            setError('Failed to load resource review list. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentResource = async () => {
        if (!reviewList || !reviewList.allResources || reviewList.allResources.length === 0) {
            return;
        }

        const currentResource = reviewList.allResources[currentResourceIndex];
        if (!currentResource) {
            return;
        }

        try {
            setLoadingResource(true);
            const resourceData = await dailyReviewService.getResourceReviewDetail(
                projectId,
                currentResource.userId || currentResource.user?.id,
                date
            );
            setCurrentResourceData(resourceData);
        } catch (err) {
            console.error('Error fetching resource review detail:', err);
            setError('Failed to load resource details. Please try again.');
        } finally {
            setLoadingResource(false);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
        // Update URL to reflect the new date
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('date', newDate);
        setSearchParams(newSearchParams, { replace: true });
    };

    const handlePrevious = () => {
        if (currentResourceIndex > 0) {
            setCurrentResourceIndex(currentResourceIndex - 1);
        }
    };

    const handleNext = () => {
        if (reviewList && currentResourceIndex < reviewList.allResources.length - 1) {
            setCurrentResourceIndex(currentResourceIndex + 1);
        } else {
            // Reached the end, show completion summary
            handleComplete();
        }
    };

    const handleComplete = async () => {
        // Validate that Overall Assessment is provided
        if (!reviewNotes.overallAssessment || reviewNotes.overallAssessment.trim() === '') {
            setNotesError('Overall Assessment is required before completing the review');
            return;
        }

        setNotesError('');
        setCompleted(true);
        
        // Mark project as reviewed and send email
        if (!reviewMarked) {
            try {
                await dailyReviewService.markProjectAsReviewed(projectId, reviewNotes, date);
                setReviewMarked(true);
                
                // Send report as PDF via email
                try {
                    await dailyReviewService.sendReportEmail(projectId, date);
                    setEmailSent(true);
                } catch (emailErr) {
                    console.error('Error sending report email:', emailErr);
                    // Don't block completion if email fails
                }
            } catch (err) {
                console.error('Error marking project as reviewed:', err);
                if (err.response?.data?.error) {
                    setNotesError(err.response.data.error);
                } else {
                    setNotesError('Failed to mark project as reviewed. Please try again.');
                }
                setCompleted(false);
            }
        }
    };

    // Auto-save notes with debounce
    const handleNotesChange = useCallback((updatedNotes) => {
        setReviewNotes(updatedNotes);
        setNotesError('');
        
        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
        
        // Cancel in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new AbortController for this save
        abortControllerRef.current = new AbortController();
        const currentAbortController = abortControllerRef.current;
        
        // Set new timeout for auto-save (1.5 second debounce)
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                // Check if request was aborted before making the call
                if (currentAbortController.signal.aborted) {
                    return;
                }
                
                await dailyReviewService.saveNotes(projectId, updatedNotes, date, {
                    signal: currentAbortController.signal
                });
            } catch (error) {
                // Ignore abort errors and axios cancel errors
                if (error.name === 'AbortError' || 
                    error.name === 'CanceledError' || 
                    error.code === 'ERR_CANCELED' ||
                    (error.response && error.response.status === 0)) {
                    return;
                }
                console.error('Error auto-saving notes:', error);
                // Don't show error to user for auto-save failures
            } finally {
                // Clear the abort controller reference if this was the current request
                if (abortControllerRef.current === currentAbortController) {
                    abortControllerRef.current = null;
                }
            }
        }, 1500); // 1.5 seconds debounce
    }, [projectId, date]);

    // Cleanup timeout and abort requests on unmount or when projectId/date changes
    useEffect(() => {
        return () => {
            // Clear timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
                saveTimeoutRef.current = null;
            }
            
            // Abort in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [projectId, date]);

    const handleReturnToProject = () => {
        navigate(`/daily-work-plan-review/project/${projectId}?date=${date}`);
    };

    // Calculate releases the current resource is participating in
    // Must be called before early returns to comply with React Hooks rules
    const resourceReleases = useMemo(() => {
        if (!currentResourceData || !releases.length) return [];

        // Get all tasks for the current resource
        const allTasks = [
            ...(currentResourceData.tasks || []),
            ...(currentResourceData.unpickedTasks || [])
        ];

        // Get feature tag IDs from resource's tasks
        const resourceFeatureTagIds = new Set();
        allTasks.forEach(task => {
            if (task.feature_tag?.id) {
                resourceFeatureTagIds.add(task.feature_tag.id);
            }
        });

        if (resourceFeatureTagIds.size === 0) return [];

        // Find releases that contain these feature tags
        const participatingReleases = releases
            .map(release => {
                const releaseFeatureTagIds = (release.release_feature_tags || [])
                    .map(rft => rft.feature_tag?.id)
                    .filter(id => id);

                // Check if any of the resource's feature tags are in this release
                const hasMatchingFeatureTag = releaseFeatureTagIds.some(id => 
                    resourceFeatureTagIds.has(id)
                );

                if (!hasMatchingFeatureTag) return null;

                // Count pending tasks (open/in-progress) for this release
                const releaseTasks = allTasks.filter(task => {
                    if (!task.feature_tag?.id) return false;
                    return releaseFeatureTagIds.includes(task.feature_tag.id) &&
                           (task.status === 'open' || task.status === 'in-progress');
                });

                return {
                    ...release,
                    pendingTasksCount: releaseTasks.length
                };
            })
            .filter(release => release !== null)
            .sort((a, b) => {
                // Sort by target_date (upcoming first), then by name
                if (a.target_date && b.target_date) {
                    return new Date(a.target_date) - new Date(b.target_date);
                }
                if (a.target_date) return -1;
                if (b.target_date) return 1;
                return a.name.localeCompare(b.name);
            });

        return participatingReleases;
    }, [currentResourceData, releases]);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="resource-review-flow-container">
                <div className="resource-review-flow-loading">
                    <div className="loading-spinner" />
                    <p>Loading resource review list...</p>
                </div>
            </div>
        );
    }

    if (error || !reviewList) {
        return (
            <div className="resource-review-flow-container">
                <div className="resource-review-flow-error">
                    <p>{error || 'Review list not found'}</p>
                    <Button onClick={fetchReviewList}>Retry</Button>
                </div>
            </div>
        );
    }

    const totalResources = reviewList.allResources.length;
    const currentResource = reviewList.allResources[currentResourceIndex];
    const isFirst = currentResourceIndex === 0;
    const isLast = currentResourceIndex === totalResources - 1;

    if (completed) {
        const resourcesWithWorkPlans = reviewList.resourcesWithWorkPlans?.length || 0;
        const resourcesWithoutWorkPlans = reviewList.resourcesWithoutWorkPlans?.length || 0;
        const totalTasksPicked = reviewList.resourcesWithWorkPlans?.reduce((sum, r) => sum + (r.projectTaskCount || 0), 0) || 0;
        const totalUnpicked = reviewList.resourcesWithoutWorkPlans?.reduce((sum, r) => sum + (r.assignedTaskCount || 0), 0) || 0;

        return (
            <div className="resource-review-flow-container">
                <div className="resource-review-flow-header">
                    <div className="resource-review-flow-header-top">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherArrowLeft />}
                            onClick={handleReturnToProject}
                        >
                            Back to Project Review
                        </Button>
                        <div>
                            <h1 className="resource-review-flow-title">Review Complete</h1>
                            <p className="resource-review-flow-subtitle">
                                All resources have been reviewed for {reviewList.project?.name}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="resource-review-flow-completion">
                    <div className="completion-icon">
                        <FeatherCheckCircle size={64} />
                    </div>
                    <h2>Review Summary</h2>
                    <div className="completion-stats">
                        <div className="completion-stat-card">
                            <div className="completion-stat-label">Total Resources Reviewed</div>
                            <div className="completion-stat-value">{totalResources}</div>
                        </div>
                        <div className="completion-stat-card">
                            <div className="completion-stat-label">Resources with Work Plans</div>
                            <div className="completion-stat-value">{resourcesWithWorkPlans}</div>
                        </div>
                        <div className="completion-stat-card">
                            <div className="completion-stat-label">Resources without Work Plans</div>
                            <div className="completion-stat-value">{resourcesWithoutWorkPlans}</div>
                        </div>
                        <div className="completion-stat-card">
                            <div className="completion-stat-label">Total Tasks Picked</div>
                            <div className="completion-stat-value">{totalTasksPicked}</div>
                        </div>
                        <div className="completion-stat-card">
                            <div className="completion-stat-label">Total Tasks Unpicked</div>
                            <div className="completion-stat-value">{totalUnpicked}</div>
                        </div>
                    </div>
                    {reviewMarked && (
                        <div className="completion-note">
                            <FeatherCheckCircle size={16} />
                            <span>Project marked as reviewed for {new Date(date).toLocaleDateString()}</span>
                        </div>
                    )}
                    {emailSent && (
                        <div className="completion-note" style={{ marginTop: '0.5rem' }}>
                            <FeatherCheckCircle size={16} />
                            <span>Report sent via email</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                        <Button 
                            variant="neutral-tertiary"
                            icon={<FeatherPrinter />}
                            onClick={() => setShowReport(true)}
                        >
                            Print Report
                        </Button>
                        <Button onClick={handleReturnToProject} variant="primary">
                            Return to Project Review
                        </Button>
                    </div>
                    {reviewList?.project && (
                        <ProjectReviewReportModal
                            projectId={projectId}
                            projectName={reviewList.project.name}
                            projectAbbreviation={reviewList.project.abbreviation}
                            date={date}
                            isReviewed={reviewMarked}
                            reviewedAt={reviewMarked ? new Date().toISOString() : null}
                            isOpen={showReport}
                            onClose={() => setShowReport(false)}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`resource-review-flow-container ${isNotesBarExpanded ? 'notes-bar-expanded' : ''}`}>
            <div className="resource-review-flow-header">
                <div className="resource-review-flow-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={handleReturnToProject}
                    >
                        Back to Project Review
                    </Button>
                    <div>
                        <h1 className="resource-review-flow-title">Resource Review</h1>
                        <p className="resource-review-flow-subtitle">
                            {reviewList.project?.name}
                        </p>
                    </div>
                </div>
                <div className="resource-review-flow-header-actions">
                    <DateSelector date={date} onDateChange={handleDateChange} />
                </div>
            </div>

            <div className="resource-review-flow-progress">
                <div className="progress-indicator">
                    Reviewing Resource {currentResourceIndex + 1} of {totalResources}
                </div>
                <div className="progress-bar">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: `${((currentResourceIndex + 1) / totalResources) * 100}%` }}
                    />
                </div>
            </div>

            {/* Resource Releases Section */}
            {resourceReleases.length > 0 && (
                <div className="resource-review-releases-section">
                    <div className="resource-review-releases-header">
                        <Package size={18} />
                        <span className="resource-review-releases-title">Releases</span>
                        <span className="resource-review-releases-count">({resourceReleases.length})</span>
                    </div>
                    <div className="resource-review-releases-list">
                        {resourceReleases.map(release => {
                            const releaseFeatureTags = (release.release_feature_tags || [])
                                .map(rft => rft.feature_tag)
                                .filter(tag => tag);
                            
                            return (
                                <div key={release.id} className="resource-review-release-item">
                                    <div className="resource-review-release-main">
                                        <div className="resource-review-release-info">
                                            <span className="resource-review-release-name">{release.name}</span>
                                            <span className="resource-review-release-version">v{release.version}</span>
                                        </div>
                                        <div className="resource-review-release-meta">
                                            {release.target_date && (
                                                <div className="resource-review-release-date">
                                                    <Calendar size={14} />
                                                    <span>{formatDate(release.target_date)}</span>
                                                </div>
                                            )}
                                            <div className="resource-review-release-pending">
                                                <span className="resource-review-release-pending-count">
                                                    {release.pendingTasksCount}
                                                </span>
                                                <span className="resource-review-release-pending-label">
                                                    {release.pendingTasksCount === 1 ? 'pending task' : 'pending tasks'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {releaseFeatureTags.length > 0 && (
                                        <div className="resource-review-release-features">
                                            {releaseFeatureTags.map(featureTag => (
                                                <FeatureTag
                                                    key={featureTag.id}
                                                    name={featureTag.name}
                                                    color={featureTag.color || '#6366f1'}
                                                    size="small"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {loadingResource ? (
                <div className="resource-review-flow-loading-resource">
                    <div className="loading-spinner" />
                    <p>Loading resource details...</p>
                </div>
            ) : currentResourceData ? (
                <>
                    {notesError && (
                        <div className="resource-review-flow-error" style={{ marginBottom: '1rem', padding: '1rem', background: '#fef2f2', border: '1px solid #dc2626', borderRadius: '6px' }}>
                            <p style={{ color: '#dc2626', margin: 0 }}>{notesError}</p>
                        </div>
                    )}
                    <ResourceReviewCard 
                        resourceData={currentResourceData} 
                        hasWorkPlan={currentResourceData?.hasWorkPlan || false}
                        projectId={projectId}
                        date={date}
                        onRefresh={fetchCurrentResource}
                    />
                    
                    <div className="resource-review-flow-navigation">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherChevronLeft />}
                            onClick={handlePrevious}
                            disabled={isFirst}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="primary"
                            iconRight={<FeatherChevronRight />}
                            onClick={handleNext}
                        >
                            {isLast ? 'Complete Review' : 'Next'}
                        </Button>
                    </div>
                </>
            ) : (
                <div className="resource-review-flow-error">
                    <p>Failed to load resource details</p>
                    <Button onClick={fetchCurrentResource}>Retry</Button>
                </div>
            )}

            <NotesBottomBar
                notes={reviewNotes}
                onNotesChange={handleNotesChange}
                onExpandedChange={setIsNotesBarExpanded}
                projectId={projectId}
                date={date}
            />
        </div>
    );
};

export default ResourceReviewFlow;

